export interface HelpArticleData {
  title: string;
  description: string;
  content: string;
}

export const helpArticles: Record<string, HelpArticleData> = {
  'creating-your-first-aspiration': {
    title: 'Creating Your First Aspiration',
    description: 'Learn how to create and manage your aspirations in GodMode.',
    content: `
      <h2>What is an Aspiration?</h2>
      <p>An aspiration represents your big-picture dreams and long-term goals. Think of it as the destination you're working toward - like "Become financially independent" or "Master a new language".</p>

      <h2>Creating an Aspiration</h2>
      <ol>
        <li>Navigate to the <strong>Aspirations</strong> page from the sidebar</li>
        <li>Click the <strong>New Aspiration</strong> button in the header</li>
        <li>Fill in the aspiration details:
          <ul>
            <li><strong>Title</strong>: A clear, inspiring name for your aspiration</li>
            <li><strong>Description</strong>: Additional details about what this means to you</li>
            <li><strong>Category</strong>: Choose from Personal, Professional, Health, Financial, Education, or Relationships</li>
            <li><strong>Priority</strong>: Set the importance level (High, Medium, Low)</li>
          </ul>
        </li>
        <li>Click <strong>Create</strong> to save your aspiration</li>
      </ol>

      <h2>Managing Aspirations</h2>
      <p>Once created, you can:</p>
      <ul>
        <li><strong>Edit</strong>: Click the edit icon to modify details</li>
        <li><strong>Reorder</strong>: Drag and drop to change priority order</li>
        <li><strong>Delete</strong>: Remove aspirations you no longer want to track</li>
        <li><strong>View Goals</strong>: Click on an aspiration to see linked goals</li>
      </ul>

      <h2>Tips for Effective Aspirations</h2>
      <ul>
        <li>Keep aspirations broad but meaningful</li>
        <li>Limit to 3-5 active aspirations to maintain focus</li>
        <li>Review and update aspirations quarterly</li>
      </ul>
    `
  },

  'setting-up-goals-with-smart-criteria': {
    title: 'Setting Up Goals with SMART Criteria',
    description: 'Create effective, measurable goals using the SMART framework.',
    content: `
      <h2>Understanding SMART Goals</h2>
      <p>GodMode uses the SMART framework to help you create goals that are:</p>
      <ul>
        <li><strong>Specific</strong>: Clear and well-defined</li>
        <li><strong>Measurable</strong>: Quantifiable progress indicators</li>
        <li><strong>Achievable</strong>: Realistic and attainable</li>
        <li><strong>Relevant</strong>: Aligned with your aspirations</li>
        <li><strong>Time-bound</strong>: Has a deadline or timeframe</li>
      </ul>

      <h2>Creating a Goal</h2>
      <ol>
        <li>Navigate to the <strong>Goals</strong> page</li>
        <li>Click <strong>New Goal</strong></li>
        <li>Fill in the goal details:
          <ul>
            <li><strong>Title</strong>: What you want to achieve</li>
            <li><strong>Link to Aspiration</strong>: Connect to a parent aspiration</li>
            <li><strong>SMART Criteria</strong>: Define each SMART element</li>
            <li><strong>Deadline</strong>: Set a target completion date</li>
          </ul>
        </li>
      </ol>

      <h2>Tracking Progress</h2>
      <p>Goal progress is automatically calculated based on linked list items. As you complete tasks in your lists, your goal progress updates accordingly.</p>

      <h2>Filtering Goals</h2>
      <p>Use the filter bar to:</p>
      <ul>
        <li>Search by keyword</li>
        <li>Filter by status (In Progress, Completed, etc.)</li>
        <li>Filter by linked aspiration</li>
        <li>Show only overdue goals</li>
      </ul>
    `
  },

  'organizing-tasks-with-lists': {
    title: 'Organizing Tasks with Lists',
    description: 'Create and manage task lists to break down your goals.',
    content: `
      <h2>Lists Overview</h2>
      <p>Lists are collections of actionable items that help you achieve your goals. Each list can contain multiple items with completion tracking.</p>

      <h2>Creating a List</h2>
      <ol>
        <li>Go to the <strong>Lists</strong> page</li>
        <li>Click <strong>New List</strong></li>
        <li>Enter a title and optional description</li>
        <li>Optionally link to a goal</li>
        <li>Choose a color for visual organization</li>
      </ol>

      <h2>Managing List Items</h2>
      <p>Click on any list to view and manage its items:</p>
      <ul>
        <li><strong>Add items</strong>: Use the quick entry field at the bottom</li>
        <li><strong>Complete items</strong>: Click the checkbox to mark as done</li>
        <li><strong>Edit items</strong>: Click on an item to modify it</li>
        <li><strong>Reorder items</strong>: Drag and drop to change order</li>
        <li><strong>Delete items</strong>: Click the delete icon</li>
      </ul>

      <h2>Filtering and Sorting</h2>
      <p>Use filters to find specific lists:</p>
      <ul>
        <li>Filter by linked goal or aspiration</li>
        <li>Search by list name</li>
        <li>Sort by title, date, or completion</li>
      </ul>
    `
  },

  'using-the-calendar': {
    title: 'Using the Calendar',
    description: 'Schedule and visualize your tasks with the calendar view.',
    content: `
      <h2>Calendar Overview</h2>
      <p>The calendar provides a weekly view of your scheduled lists and events, helping you plan your time effectively.</p>

      <h2>Viewing the Calendar</h2>
      <p>Navigate to the <strong>Calendar</strong> page to see:</p>
      <ul>
        <li>Weekly view with time slots</li>
        <li>Scheduled list events shown as colored blocks</li>
        <li>Navigate between weeks using arrow buttons</li>
        <li>Return to current week with the "Today" button</li>
      </ul>

      <h2>Creating Calendar Events</h2>
      <ol>
        <li>Click the <strong>Schedule</strong> button or click on an empty time slot</li>
        <li>Select a list to schedule</li>
        <li>Choose the date and time</li>
        <li>Set duration</li>
        <li>Optionally set up recurrence</li>
      </ol>

      <h2>Managing Events</h2>
      <p>Click on any event to:</p>
      <ul>
        <li>View the associated list items</li>
        <li>Complete items directly from the calendar</li>
        <li>Track completion for that specific scheduled instance</li>
      </ul>
    `
  },

  'creating-and-using-list-templates': {
    title: 'Creating and Using List Templates',
    description: 'Save time by creating reusable list templates.',
    content: `
      <h2>What are Templates?</h2>
      <p>Templates are reusable list structures that save time when creating similar lists repeatedly. Great for routines, checklists, and recurring workflows.</p>

      <h2>Creating a Template</h2>
      <ol>
        <li>Navigate to <strong>List Templates</strong> from the sidebar</li>
        <li>Click <strong>New Template</strong></li>
        <li>Enter template details:
          <ul>
            <li><strong>Title</strong>: Name for the template</li>
            <li><strong>Description</strong>: What it's used for</li>
            <li><strong>Category</strong>: Organize templates by type</li>
          </ul>
        </li>
        <li>Add items to the template</li>
      </ol>

      <h2>Using a Template</h2>
      <ol>
        <li>Find your template in the List Templates page</li>
        <li>Click <strong>Use Template</strong></li>
        <li>A new list will be created with all template items</li>
        <li>Optionally link the new list to a goal</li>
      </ol>

      <h2>Template Categories</h2>
      <p>Organize templates by category for easy access:</p>
      <ul>
        <li>Daily Routines</li>
        <li>Weekly Reviews</li>
        <li>Project Checklists</li>
        <li>Custom categories</li>
      </ul>
    `
  },

  'setting-up-schedules': {
    title: 'Setting Up Schedules',
    description: 'Create recurring schedules for your lists.',
    content: `
      <h2>Schedules Overview</h2>
      <p>Schedules automate when lists appear on your calendar, perfect for recurring tasks and routines.</p>

      <h2>Creating a Schedule</h2>
      <ol>
        <li>Go to the <strong>Schedules</strong> page</li>
        <li>Click <strong>Create Schedule</strong></li>
        <li>Select a list to schedule</li>
        <li>Configure the schedule:
          <ul>
            <li><strong>Frequency</strong>: Daily, Weekly, Monthly</li>
            <li><strong>Days</strong>: Which days to schedule</li>
            <li><strong>Time</strong>: When the event starts</li>
            <li><strong>Duration</strong>: How long the event lasts</li>
          </ul>
        </li>
      </ol>

      <h2>Managing Schedules</h2>
      <p>From the Schedules page, you can:</p>
      <ul>
        <li><strong>Toggle active/inactive</strong>: Pause schedules temporarily</li>
        <li><strong>Edit</strong>: Modify schedule settings</li>
        <li><strong>Delete</strong>: Remove schedules permanently</li>
      </ul>

      <h2>Schedule Tips</h2>
      <ul>
        <li>Use schedules for daily routines</li>
        <li>Create weekly review schedules</li>
        <li>Set up monthly goal check-ins</li>
      </ul>
    `
  },

  'notification-settings': {
    title: 'Notification Settings',
    description: 'Configure how and when you receive notifications.',
    content: `
      <h2>Browser Notifications</h2>
      <p>GodMode can send browser notifications to remind you about scheduled tasks.</p>

      <h2>Enabling Notifications</h2>
      <ol>
        <li>Go to the <strong>Notifications</strong> page</li>
        <li>Click <strong>Enable Notifications</strong> if prompted</li>
        <li>Allow notifications in your browser when asked</li>
      </ol>

      <h2>Notification Settings</h2>
      <p>Configure your preferences in the Settings tab:</p>
      <ul>
        <li><strong>Notification timing</strong>: How far in advance to notify</li>
        <li><strong>Sound</strong>: Enable/disable notification sounds</li>
        <li><strong>Quiet hours</strong>: Set times when notifications are muted</li>
      </ul>

      <h2>Notification History</h2>
      <p>View past notifications in the History tab to see what's been sent.</p>

      <h2>Testing Notifications</h2>
      <p>Use the Test tab to send a test notification and verify everything is working correctly.</p>
    `
  },

  'progress-tracking-and-projections': {
    title: 'Progress Tracking and Projections',
    description: 'Monitor your progress and view completion trends.',
    content: `
      <h2>Progress Overview</h2>
      <p>GodMode tracks your completion progress across all levels - aspirations, goals, and lists.</p>

      <h2>Automatic Progress Calculation</h2>
      <p>Progress is calculated automatically:</p>
      <ul>
        <li><strong>List progress</strong>: Based on completed items</li>
        <li><strong>Goal progress</strong>: Average of linked list completions</li>
        <li><strong>Aspiration progress</strong>: Average of linked goal progress</li>
      </ul>

      <h2>Projections Page</h2>
      <p>The Projections page shows visual charts of your progress:</p>
      <ul>
        <li>Select entity type (Aspirations, Goals, or Lists)</li>
        <li>Choose timeframe (Week, Month, Quarter, Year, All Time)</li>
        <li>View completion trends over time</li>
      </ul>

      <h2>Completion Timeline</h2>
      <p>See when items were completed and track your productivity patterns.</p>
    `
  },

  'configuring-ai-features': {
    title: 'Configuring AI Features',
    description: 'Set up AI-powered features with your Anthropic API key.',
    content: `
      <h2>AI Features Overview</h2>
      <p>GodMode offers optional AI-powered features for schedule optimization and report generation.</p>

      <h2>Setting Up AI</h2>
      <ol>
        <li>Go to <strong>Settings</strong></li>
        <li>Find the <strong>AI Configuration</strong> section</li>
        <li>Enter your Anthropic API key</li>
        <li>Select your preferred model</li>
        <li>Click <strong>Save</strong></li>
      </ol>

      <h2>Getting an API Key</h2>
      <p>To use AI features, you need an Anthropic API key:</p>
      <ol>
        <li>Visit <strong>console.anthropic.com</strong></li>
        <li>Create an account or sign in</li>
        <li>Generate an API key</li>
        <li>Copy and paste it into GodMode settings</li>
      </ol>

      <h2>Available AI Features</h2>
      <ul>
        <li><strong>Schedule Optimization</strong>: AI suggests optimal times for your tasks</li>
        <li><strong>Reports</strong>: Generate AI-powered progress reports</li>
      </ul>

      <h2>Privacy</h2>
      <p>Your API key is stored locally in your browser. Your data is sent to Anthropic only when you use AI features.</p>
    `
  },

  'backup-and-restore-your-data': {
    title: 'Backup and Restore Your Data',
    description: 'Protect your data with backups.',
    content: `
      <h2>Backup Options</h2>
      <p>GodMode allows you to backup your data to your local device.</p>

      <h2>Creating a Backup</h2>
      <ol>
        <li>Go to <strong>Settings</strong></li>
        <li>Scroll to the <strong>Backup &amp; Restore</strong> section</li>
        <li>Click <strong>Backup to Local Device</strong></li>
        <li>Save the downloaded backup file</li>
      </ol>

      <h2>Restoring from Backup</h2>
      <ol>
        <li>Go to <strong>Settings</strong></li>
        <li>Click <strong>Restore</strong> in the Backup section</li>
        <li>Select your backup file</li>
        <li>Confirm the restore (this will replace current data)</li>
      </ol>

      <h2>What's Included in Backups</h2>
      <ul>
        <li>All aspirations, goals, and lists</li>
        <li>List templates</li>
        <li>Schedules</li>
        <li>Notification settings</li>
        <li>Completion history</li>
      </ul>

      <h2>Backup Best Practices</h2>
      <ul>
        <li>Create regular backups (weekly recommended)</li>
        <li>Store backups in a safe location</li>
        <li>Keep multiple backup versions</li>
      </ul>
    `
  },

  'managing-your-subscription': {
    title: 'Managing Your Subscription',
    description: 'View and manage your GodMode Pro subscription.',
    content: `
      <h2>Subscription Overview</h2>
      <p>GodMode Pro gives you access to premium features and unlimited usage.</p>

      <h2>Viewing Your Subscription</h2>
      <ol>
        <li>Go to <strong>Settings</strong></li>
        <li>Find the <strong>Subscription</strong> section</li>
        <li>View your current plan and status</li>
      </ol>

      <h2>Managing Your Subscription</h2>
      <p>Click <strong>Manage Subscription</strong> to:</p>
      <ul>
        <li>Update payment method</li>
        <li>View billing history</li>
        <li>Cancel or change plan</li>
      </ul>

      <h2>Trial Period</h2>
      <p>New users get a trial period to explore Pro features. Your trial status and remaining days are shown in Settings.</p>
    `
  },

  'sync-and-data-storage': {
    title: 'Sync and Data Storage',
    description: 'Understand how your data is stored and synced.',
    content: `
      <h2>Data Storage</h2>
      <p>GodMode stores your data securely in the cloud when you're signed in. This allows access from any device.</p>

      <h2>Sync Status</h2>
      <p>Check your sync status in Settings:</p>
      <ul>
        <li><strong>Synced</strong>: All changes saved to cloud</li>
        <li><strong>Syncing</strong>: Changes being uploaded</li>
        <li><strong>Offline</strong>: Working with local data</li>
      </ul>

      <h2>Offline Usage</h2>
      <p>GodMode works offline with local storage. Changes sync automatically when you reconnect.</p>

      <h2>Data Privacy</h2>
      <p>Your data is encrypted and stored securely. Only you can access your information when signed in to your account.</p>
    `
  }
};
